import * as Elements from 'elements';
import { CollapsibleWithChevron } from 'elements/components/Containers';
import { ItemType } from 'items/models';
import * as LibraryComponents from 'library/components';
import * as React from 'react';
import * as UI from 'ui/components';
import { MessageDescriptor } from 'react-intl';
import { UserAvatar } from 'user/components';
import messages from 'library/displayMessages';
import { redirectToLxSignUp } from 'auth/actions';
import { SSOComponent } from 'auth/components/elements/SSOComponent';
import { LoginModalComponent } from 'auth/components/elements/LoginModalComponent';
import { AuthProviderEnum } from 'auth/utils';


// Whether or not the demo page in enabled in this build.
// In production, this whole file gets replaced by DevDemoPage.prod.tsx
// which sets this to false. See vite.config.js for how that works.
export const devDemoPageEnabled = false;

// We don't need the strings in this file to be translated, so use this
// to make fake MessageDescriptors for any string.
// This is a type of "tagged template" and you use it like: msg`text`
function msg(strings: TemplateStringsArray, ...values: string[]): MessageDescriptor {
    let str = '';
    strings.forEach((strPiece, i) => { str += strPiece + (values[i] || ''); });
    return {defaultMessage: str, id: 'no-id'};
}

// Helper boilerplate code for this page. These two components basically render a table:
export const Demos: React.FunctionComponent<React.PropsWithChildren> = (props) => {
    return <div className='table-responsive-lg'><table className='table'><tbody>
        {props.children}
    </tbody></table></div>;
};

interface DemoProps extends React.PropsWithChildren {
    code: string;
    caption?: string;
    details?: string;
    fullWidth?: boolean;
}

export const Demo: React.FunctionComponent<DemoProps> = (props) => {
    // Clean up the code by removing leading/trailing newlines and any overall indentation
    const codeLines = props.code.replace(/(^\n+|\n+$)/, '').split('\n');
    let indentSize = 0; while (codeLines[0].charAt(indentSize) === ' ') { indentSize++; }
    const codeCleaned = codeLines.join('\n').replace(new RegExp(`^ {0,${indentSize}}`, 'mg'), '');
    const description = <>
        {props.caption ? <p>{props.caption}</p> : null}
        <code><pre>{codeCleaned}</pre></code>
        {props.details ? <p><small>{props.details}</small></p> : null}
    </>;
    return <tr>
        {props.fullWidth ?
            <td colSpan={2}>{description} <br/> {props.children}</td>
        :
            <><td style={{maxWidth: '700px'}}>{description}</td><td style={{minWidth: '350px'}}>{props.children}</td></>
        }
    </tr>;
};

/**
 * A page showing demos of our re-usable UI components
 */
export const DevDemoPage: React.FunctionComponent<{}> = (props) => {

    // State for our demos:
    const [demoCheckboxState, setDemoCheckBoxState] = React.useState(false);
    const [demoImageFieldUrl, setDemoImageFieldUrl] = React.useState('https://picsum.photos/400/180/?image=881');
    const [demoImageFieldFile, setDemoImageFieldFile] = React.useState<File>();
    const [demoImageField2File, setDemoImageField2File] = React.useState<File>();
    const [demoTimestampState, setDemoTimestampState] = React.useState(321);
    const [demoToggleSwitchState, setDemoToggleSwitchState] = React.useState(true);
    const [demoTextBoxValue, setDemoTextBoxValue] = React.useState('to boldly go');
    const [demoHtmlTextBoxValue, setDemoHtmlTextBoxValue] = React.useState(`
        <h1>where no one has</h1>
        <ul>
          <li>gone</li>
          <li>before</li>
        </ul>
    `);
    const [demoHtmlTextBoxEditorStyle, setDemoHtmlTextBoxEditorStyle] = React.useState(UI.EditorStyle.Full);
    const changeHtmlTextBoxEditorStyle = (event: React.FormEvent<HTMLSelectElement>) => {
        const key = event.currentTarget.value as keyof typeof UI.EditorStyle;
        setDemoHtmlTextBoxEditorStyle(UI.EditorStyle[key]);
    };

    const [reorderableItems, setReorderableItems] = React.useState<ReadonlyArray<UI.ReorderableListItem>>([
        {key: 'A', html: <span>Item A</span>},
        {key: 'B', html: <span>Item B</span>},
        {key: 'C', html: <span>Item C</span>},
        {key: 'D', html: <div className='container-one rounded p-1' style={{lineHeight: '150px'}}>
            Item D (tall item in a box)
        </div>},
        {key: 'E', html: <div className='container-one rounded p-1'>
            Item E. Note that items can have any height (though minimum height is 70px), and the controls
            will automatically adjust to the height of each item. If items are not very tall, the line
                        connecting their controls may not be rendered since there's no room.
        </div>},
        {key: 'F', html: <span>Placeholder item (can't be moved)</span>, isPlaceholder: true},
        {key: 'G', html: <span>Another placeholder item</span>, isPlaceholder: true},
    ]);
    const [demoHexagonCompletion, setDemoHexagonCompletion] = React.useState(3);
    const [demoPaginationPage, setDemoPaginationPage] = React.useState(15);
    const [iconsAreOutline, setIconsAreOutline] = React.useState(false);
    const [iconsAreColored, setIconsAreColored] = React.useState(true);
    const [iconOutlineColor, setIconOutlineColor] = React.useState(false);
    const [iconCaptions, setIconCaptions] = React.useState(true);
    const [tagsEditorState, setTagsEditorState] = React.useState(['Math', 'Biology', 'IT', 'Gaming']);
    const [demoTextInputValue, setTextInputValue] = React.useState('');
    const [demoInvalidTextInputValue, setInvalidTextInputValue] = React.useState('');
    const [demoPasswordTextInputValue, setPasswordTextInputValue] = React.useState('');
    const [demoPasswordTextInputActiveState, setPasswordTextInputActive] = React.useState(false);
    const [demoShowLoginModal, setDemoShowLoginModal] = React.useState(false);
    const [demoSelectionButton1Value, setDemoSelectionButton1Value] = React.useState(false);
    const [demoSelectionButton2Value, setDemoSelectionButton2Value] = React.useState(false);

    return <UI.StandardPageLayout pageTitle={msg`UI Components`}>

        <h1>General UI Guidelines</h1>
        <ul>
            <li>Try to encapsulate UI elements into re-usable React components.</li>
            <li>Put a demo of your component here on this page.</li>
            <li>Put the styles for your component in a <code>styles.scss</code> file in the {' '}
                <code>ui/components/TheComponent</code> folder.</li>
            <li>When using a component, never use SCSS to override its "internal" styles like padding,
                font size, colors, or anything else. That will make the component look different on different
                pages, and makes it hard to change the component's styles in the future without knowing how it
                will affect every place where the component is used. It's fine to adjust the outer margin or
                apply a style like <code>float: left;</code> on a specific page though.</li>
            <li>Instead, if you need to change how a component appears on a specific page, fix
                the component's shared <code>styles.scss</code> file or add a new React property to the component,
                like how the <code>&lt;Button&gt;</code> component accepts a {' '}
                <code>btnStyle='normal|primary|outline|ghost'</code> property.</li>
            <li>Avoid using edX Paragon for new UI components / pages.</li>
        </ul>

        <h1>Buttons</h1>
        Your standard LabXchange buttons:


        <Demos>
            <Demo code={`
                <UI.Button label={messages.btnLabel} />
            `}>
                <UI.Button label={msg`This is a normal button`} />
            </Demo>
            <Demo code={`
                <UI.Button btnStyle='primary' label={messages.btnLabel} />
            `}>
                <UI.Button btnStyle='primary' label={msg`This is a primary button`} />
            </Demo>
            <Demo code={`
                <UI.Button btnStyle='outline' label={messages.btnLabel} />
            `}>
                <UI.Button btnStyle='outline' label={msg`This is an outline button`} />
            </Demo>
            <Demo code={`
                <UI.Button btnStyle='link' label={messages.btnLabel} />
            `}>
                <UI.Button btnStyle='link' label={msg`This is a link button`} />
            </Demo>
            <Demo code={`
                <UI.Button label={messages.btnLabel} disabled={true} />
            `}>
                <UI.Button label={msg`This is a disabled button`} disabled={true} />
            </Demo>
            <Demo code={`
                <UI.Button btnStyle='ghost' label={messages.btnLabel} />`}
                details='(The dark background div is used to show the ghost button in its natural habitat.)'>
                <div style={{
                    backgroundColor: '#003E6B',
                    padding: '16px',
                    borderRadius: '5px',
                    display: 'flex',
                    justifyContent: 'center',
                }}>
                  <UI.Button btnStyle='ghost' label={msg`This is a ghost button`} />
               </div>
            </Demo>
            <Demo caption='Link Buttons'
                details={`
                    For accessibility reasons, anything that goes to a new URL (loads a new page)
                    must be an <a> element, even if you want it to visually look like a button.
                    This button component will do that automatically. It will also correctly use
                    <NavLink> for internal links (avoids a complete page reload) and plain <a>
                    for external links. Use newWindow={true} to force a new window for external
                    links.
                `}
                code={`
                <UI.Button label={messages.btnLabel} href='https://www.labxchange.org' />

                <UI.Button btnStyle='primary' label={messages.btnLabel} href='/' />
            `}>
                <UI.Button label={msg`This is a link styled as a button`} href='/library' />
                <br/><br/>
                <UI.Button btnStyle='primary' label={msg`This is a link styled as a button`} href='https://www.labxchange.org' />
            </Demo>
            <Demo code={`
                <UI.Button icon='alert' label={messages.btnLabel} />
            `}>
                <UI.Button icon='alert' label={msg`Button with icon`} />
            </Demo>
            <Demo code={`
                <UI.Button size='sm' icon='trashcan' label={messages.smallButton} />
                <UI.Button btnStyle='link' size='sm' icon='trashcan' label={messages.smallLinkButton} />
            `}>
                <UI.Button size='sm' icon='trashcan' label={msg`Small Button`} />
                <UI.Button btnStyle='link' size='sm' icon='trashcan' label={msg`Small Link Button`} />
            </Demo>
            <Demo
                caption='Buttons with only icons'
                details='(The label text is still used for screenreaders and as a tooltip.)'
                code={`
                <UI.Button btnStyle='primary' icon='alert' iconOnly={true} label={messages.iconButton} /> {' '}
                <UI.Button icon='trashcan' iconOnly={true} label={messages.iconButton} /> {' '}
                <UI.Button size='sm' icon='trashcan' iconOnly={true} label={messages.smallIconButton} /> {' '}
                <UI.Button btnStyle='link' size='sm' icon='trashcan' iconOnly={true} label={messages.smallIconLink} />
            `}>
                <UI.Button btnStyle='primary' icon='alert' iconOnly={true} label={msg`Icon Button`} /> {' '}
                <UI.Button icon='trashcan' iconOnly={true} label={msg`Icon Button`} /> {' '}
                <UI.Button size='sm' icon='trashcan' iconOnly={true} label={msg`Small Icon Button`} /> {' '}
                <UI.Button btnStyle='link' size='sm' icon='trashcan' iconOnly={true} label={msg`Small Icon Link`} />
            </Demo>
            <Demo code={`
                <UI.Button icon='pathway' iconPosition='right' label={messages.btnLabel} />
            `}>
                <UI.Button icon='pathway' iconPosition='right' label={msg`Button with icon on right`} />
            </Demo>

            <Demo
                caption='Inline download button with icon'
                details={
                    `(This is an inline download button which means that ` +
                    `it is not intented to be used for large file.)`}
                code={`
                    <UI.DownloadButton
                        icon='desktop-download'
                        label={messages.btnLabel}
                        contentType='text/csv'
                        getFileName={() => 'example.csv'}
                        getValue={() => new Promise(function(resolve, reject) {
                            resolve('done');
                        })}
                    />
                `}>
                <UI.DownloadButton
                    icon='desktop-download'
                    label={msg`Download`}
                    contentType='text/csv'
                    getFileName={() => 'example.csv'}
                    getValue={() => new Promise((resolve, reject) => {
                        resolve('done');
                    })}
                />
            </Demo>
            <Demo
                caption='SSO buttons'
                code={`
                    <UI.SSOButton
                    label={msg'Continue with Facebook'}
                    icon='facebook-color'
                    />
                    <UI.SSOButton
                        label={msg'Continue with Google'}
                        icon='google'
                    />
                    <UI.SSOButton
                        label={msg'Continue with Clever'}
                        icon='clever'
                    />
                    <UI.SSOButton
                        label={msg'Continue with Classlink'}
                        icon='classlink'
                    />
                    <UI.SSOButton
                        label={msg'Continue with Email'}
                        icon='email'
                    />
                `}>
                <UI.SSOButton
                    label={msg`Continue with Facebook`}
                    icon='facebook-color'
                />
                <br/><br/>
                <UI.SSOButton
                    label={msg`Continue with Google`}
                    icon='google'
                />
                <br/><br/>
                <UI.SSOButton
                    label={msg`Continue with Clever`}
                    icon='clever'
                />
                <br/><br/>
                <UI.SSOButton
                    label={msg`Continue with Classlink`}
                    icon='classlink'
                />
                <br/><br/>
                <UI.SSOButton
                    label={msg`Continue with Email`}
                    icon='email'
                />
            </Demo>
            <Demo
                caption='Selection buttons'
                code={`
                    <UI.SelectionButton
                        label={msg'I am a learner'}
                        icon='teacher-hat'
                        selected={demoSelectionButton1Value}
                        onClick={() => {
                            setDemoSelectionButton1Value(true);
                            setDemoSelectionButton2Value(false);
                        }}
                    />
                    <UI.SelectionButton
                        label={msg'I am an educator'}
                        icon='apple-educator'
                        selected={demoSelectionButton2Value}
                        onClick={() => {
                            setDemoSelectionButton1Value(false);
                            setDemoSelectionButton2Value(true);
                        }}
                    />
                `}>
                <UI.SelectionButton
                    label={msg`I am a learner`}
                    icon={demoSelectionButton1Value ? 'teacher-hat-white' : 'teacher-hat'}
                    selected={demoSelectionButton1Value}
                    onClick={() => {
                        setDemoSelectionButton1Value(true);
                        setDemoSelectionButton2Value(false);
                    }}
                />
                <br/><br/>
                <UI.SelectionButton
                    label={msg`I am an educator`}
                    icon={demoSelectionButton2Value ? 'apple-educator-white' : 'apple-educator'}
                    selected={demoSelectionButton2Value}
                    onClick={() => {
                        setDemoSelectionButton1Value(false);
                        setDemoSelectionButton2Value(true);
                    }}
                />
            </Demo>
        </Demos>

        <h1>Navigation</h1>
        Your standard LabXchange navigation bars:

        <Demos>
            <Demo code={`
                <UI.SmallTarBar>
                    <UI.SmallTabButton label={messages.btnLabel} onClick={() => {/* */}}/>
                    <UI.SmallTabButton active={true} label={messages.btnLabel} onClick={() => {/* */}}/>
                    <UI.SmallTabButton label={messages.btnLabel} onClick={() => {/* */}}/>
                </UI.SmallTarBar>
            `}>
                <UI.SmallTabBar>
                    <UI.SmallTabButton
                        label={msg`Unselected tab`}
                        onClick={() => {/* */}}/>
                    <UI.SmallTabButton
                        active={true} label={msg`Selected tab`}
                        onClick={() => {/* */}}/>
                    <UI.SmallTabButton
                        label={msg`Unselected tab`}
                        onClick={() => {/* */}}/>
                </UI.SmallTabBar>
            </Demo>
        </Demos>

        <h1>Badges</h1>
        Your standard LabXchange badges:

        <Demos>
            <Demo code={`
                <UI.Badge iconName='lock' text={messages.badgeText} />
            `}>
                <UI.Badge iconName='lock' text={msg`Badge`} />
            </Demo>

            <Demo
                code={`<UI.Badge iconName='classroom' text={messages.badgeText} alternativeStyle={true}/>`}
                details='(The background div is used to show the transparent badge background.)'>
                <div style={{
                    backgroundColor: '#003E6B',
                    padding: '16px',
                    borderRadius: '5px',
                    display: 'flex',
                    justifyContent: 'center',
                }}>
                    <UI.Badge iconName='classroom' text={msg`Badge`} alternativeStyle={true}/>
                </div>
            </Demo>
        </Demos>

        <h1>Form Controls</h1>

        <Demos>
            <Demo caption='A TextInput' code={`
                <UI.TextInput
                    key='demo-page-input'
                    value={demoTextInputValue}
                    onChangeValue={setTextInputValue}
                    type='text'
                    placeholder={msg'Email'}
                />
            `}>
                <UI.TextInput
                    inputKey='demo-page-input-1'
                    value={demoTextInputValue}
                    onChangeValue={setTextInputValue}
                    type='text'
                    placeholder={msg`Email`}
                />
            </Demo>

            <Demo caption='A invalid TextInput' code={`
                <UI.TextInput
                    key='demo-page-input'
                    value={demoTextInputValue}
                    onChangeValue={setTextInputValue}
                    type='text'
                    placeholder={msg'Email'}
                    invalid={true}
                    feedbackMessage={msg'Email is in the wrong format.'}
                />
            `}>
                <UI.TextInput
                    inputKey='demo-page-input-2'
                    value={demoInvalidTextInputValue}
                    onChangeValue={setInvalidTextInputValue}
                    type='text'
                    placeholder={msg`Email`}
                    invalid={true}
                    feedbackMessage={msg`Email is in the wrong format.`}
                />
            </Demo>

            <Demo caption='A password TextInput' code={`
                <UI.TextInput
                    key='demo-page-input-3'
                    value={demoPasswordTextInputValue}
                    onChangeValue={setPasswordTextInputValue}
                    type={demoPasswordTextInputActiveState ? 'text' : 'password'}
                    placeholder={msg'Password'}
                    inputAccessory={
                        <UI.ShowPasswordButton
                            active={demoPasswordTextInputActiveState}
                            onClick={setPasswordTextInputActive}
                        />
                    }
                />
            `}>
                <UI.TextInput
                    inputKey='demo-page-input-3'
                    value={demoPasswordTextInputValue}
                    onChangeValue={setPasswordTextInputValue}
                    type={demoPasswordTextInputActiveState ? 'text' : 'password'}
                    placeholder={msg`Password`}
                    inputAccessory={
                        <UI.ShowPasswordButton
                            active={demoPasswordTextInputActiveState}
                            onClick={setPasswordTextInputActive}
                        />
                    }
                />
            </Demo>

            <Demo caption='A date TextInput' code={`
                <UI.TextInput
                    key='demo-page-input-4'
                    value=''
                    onChangeValue={() => {}}
                    type='date'
                    placeholder={msg'Birthdate'}
                />
            `}>
                <UI.TextInput
                    inputKey='demo-page-input-4'
                    value=''
                    onChangeValue={() => {/* */}}
                    type='date'
                    placeholder={msg`Birthdate`}
                />
            </Demo>

            <Demo caption='A Checkbox' code={`
                <UI.CheckBox
                    checked={demoCheckboxState}
                    onClick={setDemoCheckBoxState}
                ><WrappedMessage message={messages.imACheckbox} /></UI.CheckBox>
            `}>
                <UI.CheckBox
                    checked={demoCheckboxState}
                    onClick={setDemoCheckBoxState}
                >I'm a checkbox.</UI.CheckBox>
            </Demo>
            <Demo caption='Date input' code={`
                <UI.DateInput
                    defaultValue={new UI.CalendarDate(2020, 8, 15)}
                    label={messages.enterDate}
                    onChange={() => {/* */}}
                />
            `}>
                <UI.DateInput
                    defaultValue={new UI.CalendarDate(2020, 8, 15)}
                    label={msg`Enter Date`}
                    onChange={() => {/* */}}
                />
            </Demo>
            <Demo caption='Image Field (supports drag and drop)' code={`
                <UI.ImageField
                    required={true}
                    showErrors={false}
                    currentUrl={demoImageFieldUrl}
                    currentFile={demoImageFieldFile}
                    onChange={(newFile: File|undefined, newUrl: string) => {
                        setDemoImageFieldFile(newFile);
                        setDemoImageFieldUrl(newUrl);
                    }}
                />
            `}>
                <UI.ImageField
                    required={true}
                    showErrors={false}
                    currentUrl={demoImageFieldUrl}
                    currentFile={demoImageFieldFile}
                    onChange={(newFile: File|undefined, newUrl: string) => {
                        setDemoImageFieldFile(newFile);
                        setDemoImageFieldUrl(newUrl);
                    }}
                />
            </Demo>
            <Demo caption='Image Field (empty)' code={`
                <UI.ImageField
                    required={true}
                    showErrors={false}
                    currentUrl={''}
                    currentFile={demoImageField2File}
                    onChange={(newFile: File|undefined, newUrl: string) => {
                        setDemoImageField2File(newFile);
                    }}
                />
            `}>
                <UI.ImageField
                    required={true}
                    showErrors={false}
                    currentUrl={''}
                    currentFile={demoImageField2File}
                    onChange={(newFile: File|undefined, newUrl: string) => {
                        setDemoImageField2File(newFile);
                    }}
                />
            </Demo>
            <Demo caption='TextBox' code={`
                <UI.TextBox
                    label={<WrappedMessage message={messages.textboxLabel} />}
                    value={demoTextBoxValue}
                    onChange={(v) => { setDemoTextBoxValue(v); }}
                />
            `}>
                <UI.TextBox
                    label={'This is a text box.'}
                    value={demoTextBoxValue}
                    onChange={(v) => { setDemoTextBoxValue(v); }}
                />
            </Demo>
            <Demo caption='HtmlTextBox' code={`
                <UI.HtmlTextBox
                    label={<WrappedMessage message={messages.textboxLabel} />}
                    defaultValue={'${demoHtmlTextBoxValue}'}
                    editorStyle={UI.EditorStyle.${UI.EditorStyle[demoHtmlTextBoxEditorStyle]}}
                    height={45}
                    onChange={(e) => { setDemoHtmlTextBoxValue(e); }}
                />
            `}>
                <UI.HtmlTextBox
                    label={'This is an html text box.'}
                    defaultValue={demoHtmlTextBoxValue}
                    editorStyle={demoHtmlTextBoxEditorStyle}
                    height={45}
                    onChange={(e) => { setDemoHtmlTextBoxValue(e); }}
                />
                <label>Choose editor style:
                <select value={UI.EditorStyle[demoHtmlTextBoxEditorStyle]}
                    onBlur={changeHtmlTextBoxEditorStyle}
                    onChange={changeHtmlTextBoxEditorStyle}
                >{
                    Object.keys(UI.EditorStyle).filter((key) => {
                        return !isNaN(Number(key));
                    }).map((key) => {
                        const value = UI.EditorStyle[key as any];
                        return <option key={key} value={value}>{value}</option>;
                    })
                }</select></label>
            </Demo>
            <Demo caption='Toggle switch' code={`
                <UI.ToggleSwitch
                    isChecked={demoToggleSwitchState}
                    onToggleSwitchSelect={setDemoToggleSwitchState}
                    displayText='A Toggle Switch'
                />
            `}>
                <UI.ToggleSwitch
                    isChecked={demoToggleSwitchState}
                    onToggleSwitchSelect={setDemoToggleSwitchState}
                    displayText='A Toggle Switch'
                />
            </Demo>
            <Demo code={`
                <UI.ToggleSwitch
                    isChecked={false}
                    onToggleSwitchSelect={() => {/* */}}
                    disabled={true}
                    displayText='A Disabled Toggle Switch'
                />
            `}>
                <UI.ToggleSwitch
                    isChecked={false}
                    onToggleSwitchSelect={() => {/* */}}
                    disabled={true}
                    displayText='A Disabled Toggle Switch'
                />
            </Demo>
            <Demo caption='Toggle text switch' code={`
                <UI.ToggleTextSwitch
                    isChecked={true}
                    onToggle={() => {/* */}}
                />
            `}>
                <UI.ToggleTextSwitch
                    isChecked={true}
                    onToggle={() => {/* */}}
                />
            </Demo>
            <Demo caption='Timestamp Input (for video)' code={`
                <UI.TimestampInput timeStamp={demoTimestampState} onTimeStampChange={setDemoTimestampState} />
            `}>
                <UI.TimestampInput timeStamp={demoTimestampState} onTimeStampChange={setDemoTimestampState} />
            </Demo>

            <Demo caption='Reorderable List' code={`
                const [reorderableItems, setReorderableItems] = React.useState<ReadonlyArray<UI.ReorderableListItem>>([
                    {key: 'A', html: <span>Item A</span>},
                    {key: 'B', html: <span>Item B</span>},
                    {key: 'C', html: <span>Item C</span>},
                    {key: 'D', html: <div className='container-one rounded p-1' style={{lineHeight: '150px'}}>
                        Item D (tall item in a box)
                    </div>},
                    {key: 'E', html: <div className='container-one rounded p-1'>
                        Item E. Note that items can have any height (though minimum height is 70px), and the controls
                        will automatically adjust to the height of each item. If items are not very tall, the line
                        connecting their controls may not be rendered since there's no room.
                    </div>},
                    {key: 'F', html: <span>Placeholder item (can't be moved)</span>, isPlaceholder: true},
                    {key: 'G', html: <span>Another placeholder item</span>, isPlaceholder: true},
                ]);

                <UI.ReorderableList
                    items={reorderableItems}
                    onMove={(oldIndex, newIndex) => {
                        const newItems = reorderableItems.slice(); // Shallow copy array
                        newItems.splice(oldIndex, 1); // Remove item from its old position
                        newItems.splice(newIndex, 0, reorderableItems[oldIndex]); // Insert item into its new position
                        setReorderableItems(newItems);
                    }}
                />
            `}>
                <UI.ReorderableList
                    items={reorderableItems}
                    onMove={(oldIndex, newIndex) => {
                        const newItems = reorderableItems.slice(); // Shallow copy array
                        newItems.splice(oldIndex, 1); // Remove item from its old position
                        newItems.splice(newIndex, 0, reorderableItems[oldIndex]); // Insert item into its new position
                        setReorderableItems(newItems);
                    }}
                />
            </Demo>
            <Demo caption='Tags' code={`
                <UI.Tag
                    onClick={() => {/* */}}
                    hoverType="color"
                    disableIcon={true}
                    children={"Tag 1"}
                />
                <UI.Tag
                    onClick={() => {/* */}}
                    hoverType="underline"
                    disableIcon={false}
                    children={"Tag 2"}
                />
                <UI.Tag
                    children={"Tag 3"}
                />
            `}>
                <UI.Tag
                    onClick={() => {/* */}}
                    hoverType='color'
                    disableIcon={true}
                    children={'Tag 1'}
                />
                <UI.Tag
                    onClick={() => {/* */}}
                    hoverType='underline'
                    disableIcon={false}
                    children={'Tag 2'}
                />
                <UI.Tag
                    children={'Tag 3'}
                />
            </Demo>
            <Demo caption='Tag editor' code={`
                <UI.TagsEditor
                    placeholder={messages.pathwayTagsAddButton}
                    required={false}
                    showErrors={true}
                    onChange={(tags) => {setTagsEditorState(tags)}}
                    tags={tagsEditorState}
                />
            `}>
                <UI.TagsEditor
                    placeholder={messages.pathwayTagsAddButton}
                    required={false}
                    showErrors={true}
                    onChange={(tags) => {setTagsEditorState(tags);}}
                    tags={tagsEditorState}
                />
            </Demo>
            <Demo caption='Search bar' code={`
                <UI.SearchBar
                    onSubmit={(event) => {}}
                    searchMessage={messages.filterToggle}
                    searchButtonLabel={messages.filterToggle}
                />
            `}>
                <UI.SearchBar
                    onSubmit={() => {/* */}}
                    searchMessage={messages.filterToggle}
                    searchButtonLabel={messages.filterToggle}
                />
            </Demo>
        </Demos>

        <h1>Modals</h1>
        <Demos>
            <Demo caption={'Error message'} code={`
                <UI.Button onClick={() => {
                    UI.showErrorMessage(<WrappedMessage message={messages.youWantedToSeeAnError} />);
                }} icon='issue-opened' label={messages.showAnError} />
            `}>
                <UI.Button onClick={() => {
                    UI.showErrorMessage('You wanted to see an error');
                }} icon='issue-opened' label={msg`Show an error message`} />
            </Demo>
            <Demo code={`
                <UI.Button onClick={() => {
                    UI.showErrorMessage(
                        <WrappedMessage message={messages.youWantedToSeeAnError} />,
                        {title: <WrappedMessage message={messages.customTitle} />}
                    );
                }} icon='issue-opened' label={messages.showAnError} />
            `}>
                <UI.Button onClick={() => {
                    UI.showErrorMessage('You wanted to see an error', {title: 'Custom Title'});
                }} icon='issue-opened' label={msg`Error with custom title`} />
            </Demo>
            <Demo caption={'Warning message'} code={`
                <UI.Button onClick={() => {
                    UI.showWarningMessage('Be careful');
                }} icon='stop' label={messages.showWarningMsg} />
            `}>
                <UI.Button onClick={() => {
                    UI.showWarningMessage('Be careful');
                }} icon='stop' label={msg`Show warning message`} />
            </Demo>
            <Demo caption={'Success message'} code={`
                <UI.Button onClick={() => {
                    UI.showSuccessMessage('That worked');
                }} icon='info' label={messages.showSuccessMsg} />
            `}>
                <UI.Button onClick={() => {
                    UI.showSuccessMessage('That worked');
                }} icon='info' label={msg`Show success message`} />
            </Demo>
            <Demo caption={'Login Required'} code={`
                <UI.Button onClick={() => {
                    UI.showLoginRequiredMessage();
                }} icon='lock' label={messages.showLoginRequired} />
            `}>
                <UI.Button onClick={() => {
                    UI.showLoginRequiredMessage();
                }} icon='lock' label={msg`Show login required`} />
            </Demo>
            <Demo caption={'Login Required (component)'} code={`
                <UI.RedirectToLogin />
            `}>
                (no demo; this is the component/declarative form of <code>UI.showLoginRequiredMessage()</code>)
            </Demo>
            <Demo caption={'Login Modal (component)'} code={`
                <UI.Button onClick={() => {
                    setDemoShowLoginModal(true)
                }} icon='lock' label={msg'Show login required'} />

                {demoShowLoginModal &&
                    <LoginModalComponent
                        title={msg'WELCOME BACK'}
                        subtitle={msg'Sign in with email.'}
                        termsAndPolicyMessage={msg'Continue'}
                        onRequestClose={() => {setDemoShowLoginModal(false)}}
                        onSignUpButtonClick={redirectToLxSignUp}
                    >
                        <SSOComponent
                            providers={['facebook', 'google', 'clever', 'classlink', 'email', 'edx']}
                            topMessage={msg'You have to be over 13 years old to register on LabXchange.'}
                            onCheck={() => {/* */}}
                            bottomMessage={msg'Subscribe to our newsletter for periodic updates.'}
                        />
                    </LoginModalComponent>
                }
            `}>
                <UI.Button onClick={() =>
                    setDemoShowLoginModal(true)
                } label={msg`Show login modal`} />
                {demoShowLoginModal &&
                    <LoginModalComponent
                        title={msg`WELCOME BACK`}
                        subtitle={msg`Sign in with email.`}
                        ariaLabel={msg`Sign in`}
                        termsAndPolicyMessage={msg`Continue`}
                        onRequestClose={() => setDemoShowLoginModal(false)}
                        onSignUpButtonClick={redirectToLxSignUp}
                    >
                        <SSOComponent
                            providers={Object.values(AuthProviderEnum)}
                            topMessage={msg`You have to be over 13 years old to register on LabXchange.`}
                            onCheck={() => {/* */}}
                            bottomMessage={msg`Subscribe to our newsletter for periodic updates.`}
                        />
                    </LoginModalComponent>
                }
            </Demo>
        </Demos>

        <h1>Headings &amp; Text</h1>
        <Demos>
            <Demo caption={`
                H1 heading : top-level heading, only 1 allowed per page.
            `} code={`
                <h1><WrappedMessage message={messages.headingTitle}/></h1>
            `}>
                <h1>H1: Heading 1</h1>
            </Demo>
            <Demo caption={`
                H2 heading : 2nd-level heading
            `} code={`
                <h2><WrappedMessage message={messages.headingTitle}/></h2>
            `}>
                <h2>H2: Heading 2</h2>
            </Demo>
            <Demo caption={`
                H3 heading : 3rd-level heading
            `} code={`
                <h3><WrappedMessage message={messages.headingTitle}/></h3>
            `}>
                <h3>H3: Heading 3</h3>
            </Demo>
            <Demo caption={`
                H4 heading : 4th-level heading
            `} code={`
                <h4><WrappedMessage message={messages.headingTitle}/></h4>
            `}>
                <h4>H4: Heading 4</h4>
            </Demo>
            <Demo caption={`
                H5 heading : 5th-level heading
            `} code={`
                <h5><WrappedMessage message={messages.headingTitle}/></h5>
            `}>
                <h5>H5: Heading 5</h5>
            </Demo>
            <Demo caption={`
                H6 heading : 6th-level heading
            `} code={`
                <h6><WrappedMessage message={messages.headingTitle}/></h6>
            `}>
                <h6>H6: Heading 6</h6>
            </Demo>
            <Demo caption={`
                Paragraph text
            `} code={`
                <p><WrappedMessage message={messages.paragraphText}/></p>
            `}>
                <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore
                   et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut
                   aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse
                   cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa
                   qui officia deserunt mollit anim id est laborum.</p>
          </Demo>
        </Demos>
        <h1>Sections</h1>
        <Demos>
            <Demo caption={`
                Item section: Encapsulates components in a section with a title, designed to be used in
                edit item pages.
            `} code={`
                <UI.ItemSection
                    title={<WrappedMessage message={messages.sectionTitle} />}
                    sectionName={'section1'}
                    isMandatory={true}
                >
                    Content or controls here.
                </UI.ItemSection>
            `}>
                <UI.ItemSection
                    title={'Section Title'}
                    sectionName={'section1'}
                    isMandatory={true}
                >
                    Content or controls here.
                </UI.ItemSection>
            </Demo>
            <Demo caption={`
                Container One
            `} code={`
                <UI.ContainerOne>
                    <div className='p-3'>
                        Content in a container. The container has no padding (in case you want to put an
                        image etc. inside), so use a div.p-3 to add padding if needed.
                    </div>
                </UI.ContainerOne>
            `}>
                <UI.ContainerOne>
                    <div className='p-3'>
                        Content in a container. The container has no padding (in case you want to put an
                        image etc. inside), so use a div.p-3 to add padding if needed.
                    </div>
                </UI.ContainerOne>
            </Demo>
            <Demo caption={`
                Container One with Inset
            `} code={`
                <UI.ContainerOne>
                    <p className='p-3'>
                        A container can have an inset at the top or the bottom, which has a darker background color.
                        You can use the <strong>justify</strong> property to set
                        <a href='https://getbootstrap.com/docs/4.0/utilities/flex/#justify-content'>
                        various alignment options</a>, and mix in any other
                        <a href='https://getbootstrap.com/docs/4.0/utilities/flex/'>utility classes</a>
                        using <strong>otherClasses</strong> for even more control.
                    </p>
                    <UI.ContainerInset justify='end'>
                        <span>I'm on the right.</span>
                    </UI.ContainerInset>
                </UI.ContainerOne>
            `}>
                <UI.ContainerOne>
                    <p className='p-3'>
                        A container can have an inset at the top or the bottom, which has a darker background color.
                        You can use the <strong>justify</strong> property to set{' '}
                        <a href='https://getbootstrap.com/docs/4.0/utilities/flex/#justify-content'>
                        various alignment options</a>, and mix in any other{' '}
                        <a href='https://getbootstrap.com/docs/4.0/utilities/flex/'>utility classes</a>{' '}
                        using <strong>otherClasses</strong> for even more control.
                    </p>
                    <UI.ContainerInset justify='end'>
                        <span>I'm on the right.</span>
                    </UI.ContainerInset>
                </UI.ContainerOne>
            </Demo>
            <Demo caption={`
                CollapsibleWithChevron (seems unstyled?)
            `} code={`
                <CollapsibleWithChevron
                    title={'Collapsible with chevron'}
                    defaultOpen={true}
                >
                    <p>Content here.</p>
                </CollapsibleWithChevron>
            `}>
                <CollapsibleWithChevron
                    title={'Collapsible with chevron'}
                    defaultOpen={true}
                >
                    <p>Content here.</p>
                </CollapsibleWithChevron>
            </Demo>
            <Demo caption={`
                ThinItemSection: An ItemSection alternative for cases where we don't want the the fancy styles
                with a title. Use to ensure we have a consistent padding, etc. between items.
            `} code={`
                <UI.ThinItemSection>
                    Content or controls here.
                </UI.ThinItemSection>
            `}>
                <UI.ThinItemSection>
                    Content or controls here.
                </UI.ThinItemSection>
            </Demo>
        </Demos>

        <h1>Users</h1>
        <Demos>
            <Demo caption='User avatar' code={`
                <UserAvatar username='staff' diameter='40px' />
            `}>
                <UserAvatar username='staff' diameter='40px' />
            </Demo>
            <Demo caption='Add content to favorites' code={`
                <UI.FavoriteButton
                    itemId={'item-key'}
                    isFavorite={false}
                    showLabel={true}
                    btnStyle={small}
                />
            `}>
                <UI.FavoriteButton
                    itemId={'item-key'}
                    isFavorite={false}
                    showLabel={true}
                    btnStyle='small'
                />
            </Demo>
        </Demos>

        <h1>Miscellaneous Components</h1>
        <Demos>
            <Demo caption='Loading Spinner' code={`
                <UI.Spinner />
            `}>
                <UI.Spinner />
            </Demo>
            <Demo
                caption='Hexagon: put an image or text onto a hexagon'
                details='(Make a hexagon <Icon> if you just want to display a plain hexagon)'
                code={`
                <UI.Hexagon width={100} height={100} text='' img='https://picsum.photos/200/200/?image=881' />
            `}>
                <UI.Hexagon width={100} height={100} text='' img='https://picsum.photos/200/200/?image=881' />
            </Demo>
            <Demo caption={'Hexagon Completion'} code={`
                <UI.HexagonCompletion current={${demoHexagonCompletion}} total={15} />
            `}>
                <UI.HexagonCompletion current={demoHexagonCompletion} total={15} />
                <br/>
                Adjust to see different completion:<br/>
                <input
                    type='range' min='0' max='15' value={demoHexagonCompletion}
                    onChange={(e) => { setDemoHexagonCompletion(e.target.valueAsNumber); }}
                />
            </Demo>
            <Demo caption='Pagination' code={`
                <UI.Pagination currentPage={${demoPaginationPage}} pageCount={30} onPageSelect={setDemoPaginationPage}/>
            `}>
                <UI.Pagination currentPage={demoPaginationPage} pageCount={30} onPageSelect={setDemoPaginationPage}/>
            </Demo>
            <Demo caption='Jump To Page' code={`
                <UI.JumpTo currentPage={${demoPaginationPage}} pageCount={30} onPageSelect={setDemoPaginationPage} />
            `}>
                <UI.JumpTo currentPage={demoPaginationPage} pageCount={30} onPageSelect={setDemoPaginationPage} />
            </Demo>
            <Demo caption='Full date' code={`
                <Elements.FullDate date={new Date()}/>
            `}>
                <Elements.FullDate date={new Date()}/>
            </Demo>
            <Demo caption='Alert box' code={`
                <UI.AlertBox type='warning' bodyMessage={msg'This item will be updated on June'}/>
                <br/><br/>
                <UI.AlertBox type='success' bodyMessage={msg'Account created!'} showCloseIcon={true}/>
            `}>
                <UI.AlertBox type='warning' bodyMessage={msg`This item will be updated on June`}/>
                <br/><br/>
                <UI.AlertBox type='success' bodyMessage={msg`Account created!`} showCloseIcon={true}/>
            </Demo>
        </Demos>

        <h1>Icons</h1>
        <Demos>
            <Demo caption='A regular icon' code={`
                <UI.Icon name='cluster' zoom='50' />
            `}>
                <UI.Icon name='cluster' zoom='50' />
            </Demo>
            <Demo caption='Change icon color' code={`
                <span style={{color: 'red'}}><UI.Icon name='cluster' zoom='50' /></span>
            `}>
                <span style={{color: 'red'}}><UI.Icon name='cluster' zoom='50' /></span>
            </Demo>
            <Demo caption='Icon on a button' code={`
                <UI.Button icon='plus' label={messages.add} />
            `}>
                <UI.Button icon='plus' label={msg`Add`} />
            </Demo>
            <Demo caption='LabXchange Icons' code={`
                pathway, cluster, library
            `}>
                <UI.Icon name='pathway' zoom='50' title={msg`pathway`} />
                <UI.Icon name='cluster' zoom='50' title={msg`cluster`} />
                <UI.Icon name='library' zoom='50' title={msg`library`} />
            </Demo>
            <Demo caption='All icons - hover over to see icon name in tooltip' code='' fullWidth={true}>
                <UI.ToggleSwitch
                    isChecked={iconsAreOutline}
                    onToggleSwitchSelect={setIconsAreOutline}
                    displayText={iconsAreOutline ? '"outline" icons' : '"solid" icons'}
                />
                <UI.ToggleSwitch
                    isChecked={iconsAreColored}
                    onToggleSwitchSelect={setIconsAreColored}
                    displayText={iconsAreColored ? 'brightly colored icons' : 'default "currentColor" icons'}
                />
                <UI.ToggleSwitch
                    isChecked={iconOutlineColor}
                    onToggleSwitchSelect={setIconOutlineColor}
                    displayText={iconOutlineColor ? 'Yellow icon outline color' : 'default icon outline color'}
                />
                <UI.ToggleSwitch
                    isChecked={iconCaptions}
                    onToggleSwitchSelect={setIconCaptions}
                    displayText={iconCaptions ? 'icon captions' : 'no icon captions'}
                />
                <div style={{marginTop: '16px', display: 'grid', gridGap: '20px',
                    gridTemplateColumns: `repeat(auto-fill, ${iconCaptions ? '70px' : '50px'})`}} >
                {Object.keys(UI.ICONS).map((iconName: any) =>
                    <React.Fragment key={iconName}>
                        <div style={{textAlign: 'center'}}>
                            <UI.Icon
                                name={iconName}
                                fill={iconsAreColored ? '#00AEEF' : 'currentColor'}
                                outline={iconsAreOutline}
                                title={{defaultMessage: iconName, id: iconName}}
                                zoom='50'
                                outlineColor={iconOutlineColor ? '#fcd05f' : undefined}
                            />
                            {iconCaptions && <div>{iconName}</div>}
                        </div>
                    </React.Fragment>
                )}
                </div>
            </Demo>

            <Demo caption='ItemMetadata Icons' code={`
                <LibraryComponents.ItemIcon itemType={'pathway' as ItemType} zoom='50'/>
                <LibraryComponents.ItemIcon itemType={ItemType.Cluster} zoom='50'/>
            `}>
                <LibraryComponents.ItemIcon itemType={'pathway' as ItemType} zoom='50'/>
                <LibraryComponents.ItemIcon itemType={ItemType.Cluster} zoom='50'/>
            </Demo>
        </Demos>
    </UI.StandardPageLayout>;
};
